import { isMobile } from 'react-device-detect';
import { makeAutoObservable, runInAction } from 'mobx';

import { CameraView, CarbonViewer, CarbonViewerConstructor, CarbonViewerSettings, ViewModeType } from '@cutwise/carbon-viewer';
import wasm from '@cutwise/carbon-viewer/carbonRendererApi.wasm?url';
import { CarbonViewerState, ViewMode } from '@cutwise/carbon-viewer/lib/constants';
import { HTTPResponse } from '@cutwise/utils';

import { urlAPI } from '@/api/urlAPI';
import { DEFAULT_COLLECTION_ID, PRODUCT_ROUTE } from '@/constants/routes';
import { Diamond } from '@/entities/Diamond';
import { DiamondsList } from '@/entities/DiamondsList';

export class DiamondsStore {
  collectionId?: string;
  collection?: DiamondsList;
  diamond?: Diamond;

  listError?: Error;
  diamondError?: Error;

  carbonViewer: Nullable<CarbonViewer> = null;
  isCarbonViewerLoaded = false;
  viewMode = ViewMode.Fire;
  viewerError: Nullable<string> = null;

  isDebug = false;
  isContactsPopupOpen = false;

  constructor() {
    makeAutoObservable(this);
  }

  get reports() {
    return this.collection?.reports ?? [];
  }

  get contacts() {
    return this.collection?.contactData;
  }

  get diamondIndex() {
    return this.reports.findIndex((report) => report.productId === this.diamond?.productId);
  }

  fetchDiamondList = async (id?: string, full = true) => {
    try {
      this.collectionId = id;

      // If "params.collectionId" is empty when load list of diamonds - means user opened home page without collection id,
      // in this case by default open "lexus" collection
      if (!this.collectionId) {
        this.collectionId = DEFAULT_COLLECTION_ID;
      }

      const response = await fetch(`${import.meta.env.MR_API_URI}/dilog/list/${this.collectionId}${full ? '?full=1' : ''}`);
      const collection = await response.json();

      runInAction(() => {
        this.collection = new DiamondsList(collection);
      });
    } catch (e) {
      this.listError = new Error(`DiLog is currently unable to show you the «<b>${this.collectionId}</b>» list.`);
    }
  };

  fetchDiamond = async (productId?: string, collectionId?: string) => {
    try {
      if (!productId) {
        throw new Error('"productId" not found');
      }

      const response = await fetch(`${import.meta.env.MR_API_URI}/dilog/${productId}`);
      const diamond = await response.json();

      runInAction(() => {
        this.diamond = new Diamond(diamond);
      });

      if (!this.collection && collectionId) {
        runInAction(() => {
          this.collectionId = collectionId;
        });

        await this.fetchDiamondList(collectionId, false);
      }
    } catch (e) {
      if (e.response?.status === HTTPResponse.Forbidden) {
        this.diamondError = new Error('Diamond is unpublished or hidden.');
      } else {
        this.diamondError = new Error(`DiLog is currently unable to provide you with information on diamond «<b>${productId}</b>».`);
      }
    }
  };

  startCarbonViewer = () => {
    if (!this.diamond?.render!.cbr) {
      return;
    }

    const { debug, toolbar } = urlAPI.parseSearchQuery(window.location.search);

    this.isDebug = typeof debug !== 'undefined';
    this.viewMode = this.diamond?.isFancyColor ? ViewMode.OfficeFancyColor : ViewMode.Fire;

    const options: CarbonViewerConstructor = {
      customPath: [
        {
          key: 'carbonRendererApi.wasm',
          path: wasm,
        },
      ],
      model: this.diamond?.render!.cbr,
      viewMode: this.viewMode,
      toolbar: typeof toolbar !== 'undefined',
      containerId: '#container',
      touchRotateDelay: 500, // 0.5 second
      touchRotateHorizontalStart: true,
      noZoom: true,
      onStateUpdate: (state: CarbonViewerState, ...data: unknown[]) => {
        if (state === CarbonViewerState.PhotorealReady) {
          runInAction(() => {
            this.isCarbonViewerLoaded = true;
          });
        }

        if (state === CarbonViewerState.Error) {
          runInAction(() => {
            this.viewerError = data[0] as string;
          });
        }
      },
      useBakeGeometryToMap: isMobile,
      toolbarAppendTo: '#root',
    };

    if (this.diamond?.render!.spectrum) {
      options.absorptionSpectrum = this.diamond.render!.spectrum;
    }

    this.carbonViewer = new CarbonViewer(options);
  };

  setViewMode = (viewMode: ViewModeType) => {
    this.viewMode = viewMode;

    this.applySettings({ viewMode: this.viewMode });
  };

  resetCamera = () => {
    this.carbonViewer?.resetCamera();
  };

  rotateCamera = (angle: number) => {
    this.carbonViewer?.rotateHorizontal(angle);
  };

  setCameraView = (view: CameraView) => {
    this.carbonViewer?.setCameraView(view);
  };

  zoomIn = () => {
    this.carbonViewer?.zoomIn(0.15);
  };

  zoomOut = () => {
    this.carbonViewer?.zoomOut(0.15);
  };

  toggleGyro = () => {
    this.applySettings({
      isGyro: !this.carbonViewer?.isGyro,
    });
  };

  blockRender = (state: boolean) => {
    this.carbonViewer?.blockRender(state);
  };

  applySettings = (settings: CarbonViewerSettings) => {
    this.carbonViewer?.apply(settings);
  };

  prevDiamond = () => {
    if (this.reports.length === 0) {
      return '';
    }

    if (!this.diamondIndex || this.diamondIndex === 0) {
      return '';
    }

    const nextIndex = this.diamondIndex - 1;
    let nextId;

    if (nextIndex === -1) {
      nextId = this.reports[this.reports.length - 1]?.id;
    } else {
      nextId = this.reports[nextIndex]?.id;
    }

    if (typeof nextId === 'undefined') {
      return '';
    }

    let nextURL = `${PRODUCT_ROUTE}/${nextId}`;

    if (this.collectionId) {
      nextURL += `/${this.collectionId}`;
    }

    return urlAPI.new(nextURL);
  };

  nextDiamond = () => {
    if (this.reports.length === 0) {
      return '';
    }

    if (this.diamondIndex === this.reports.length - 1) {
      return '';
    }

    const nextIndex = this.diamondIndex + 1;
    let nextId;

    if (nextIndex === this.reports.length) {
      nextId = this.reports[0]?.id;
    } else {
      nextId = this.reports[nextIndex]?.id;
    }

    if (typeof nextId === 'undefined') {
      return '';
    }

    let nextURL = `${PRODUCT_ROUTE}/${nextId}`;

    if (this.collectionId) {
      nextURL += `/${this.collectionId}`;
    }

    return urlAPI.new(nextURL);
  };

  dispose = () => {
    this.diamond = undefined;
    this.listError = undefined;
    this.diamondError = undefined;

    this.closeContactsPopup();

    try {
      this.carbonViewer?.dispose();
    } catch (e) {
      // do nothing
    }

    this.isCarbonViewerLoaded = false;
    this.carbonViewer = null;
    this.viewerError = null;
  };

  openContactsPopup = () => {
    this.isContactsPopupOpen = true;
  };

  closeContactsPopup = () => {
    this.isContactsPopupOpen = false;
  };
}

export const diamondsStore = new DiamondsStore();
